import React, { useRef, useEffect } from 'react';
import type { RoughAnnotationType } from 'rough-notation/lib/model';

interface Props {
  children: React.ReactNode;
  type: RoughAnnotationType;
  color?: string;
  strokeWidth?: number;
  padding?: number | [number, number] | [number, number, number, number];
  animationDuration?: number;
  delay?: number;
  triggerOnScroll?: boolean;
  scrollThreshold?: number;
  show?: boolean;
}

const RoughAnnotation: React.FC<Props> = ({
  children,
  type,
  color = '#428fe9',
  strokeWidth = 2,
  padding = 4,
  animationDuration = 800,
  delay = 0,
  triggerOnScroll = true,
  scrollThreshold = 0.3,
  show: manualShow,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const annotationRef = useRef<ReturnType<typeof import('rough-notation').annotate> | null>(null);
  const hasShown = useRef(false);

  useEffect(() => {
    if (!ref.current) return;

    // Reset so annotation can re-show after effect re-run (e.g. HMR)
    hasShown.current = false;

    let cleanup: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    import('rough-notation').then(({ annotate }) => {
      if (!ref.current) return;

      const annotation = annotate(ref.current, {
        type,
        color,
        strokeWidth,
        padding: padding as number,
        animationDuration,
        multiline: true,
      });
      annotationRef.current = annotation;

      if (manualShow !== undefined) {
        if (manualShow && !hasShown.current) {
          hasShown.current = true;
          timer = setTimeout(() => annotation.show(), delay);
        }
        cleanup = () => { clearTimeout(timer); annotation.remove(); };
        return;
      }

      if (triggerOnScroll) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting && !hasShown.current) {
              hasShown.current = true;
              timer = setTimeout(() => annotation.show(), delay);
              observer.disconnect();
            }
          },
          { threshold: scrollThreshold }
        );
        observer.observe(ref.current);
        cleanup = () => {
          clearTimeout(timer);
          observer.disconnect();
          annotation.remove();
        };
      } else {
        if (!hasShown.current) {
          hasShown.current = true;
          timer = setTimeout(() => annotation.show(), delay);
        }
        cleanup = () => { clearTimeout(timer); annotation.remove(); };
      }
    });

    return () => cleanup?.();
  }, [type, color, strokeWidth, padding, animationDuration, triggerOnScroll, scrollThreshold, delay, manualShow]);

  return <span ref={ref} style={{ display: 'inline' }}>{children}</span>;
};

export default RoughAnnotation;
